import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import cn from "classnames";
import { bytesToSize, debounce, everyTrue, hasTrue, isValidObject, isValidURL } from "@/helper/helper";
import Error from "@/components/common/Error/Error";
import LoadingDots from "@/components/common/LoadingDots/LoadingDots";
import { AiOutlineFilePdf } from "react-icons/ai";
import { BiErrorCircle } from "react-icons/bi";
import { forRender, maxFileUploadSize } from "@/constant";
import { useUserMenu } from "@/states/qr_types/menu.state";
import { useConfig, useError, useIsRequest } from "@/states/common.state";
import useFileUpload from "@/hooks/useFileUpload";
import { useHandleGenerateResult } from "@/hooks/useHandleGenerateResult";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import { useSignUpModal } from "@/states/ui.state";
import { HomePageError } from "@/components/common/HomePageError";
import classes from "./MenuHomepage.module.scss";
import { useTranslations } from "next-intl";
import useGuestFile from "@/hooks/common/useGuestFile";
import { When } from "@/components/common/When";
import IFThen from "@/components/common/IFThen";
import { useMediaQuery } from "react-responsive";
import { FiUploadCloud } from "react-icons/fi";
import { useQrShowState } from "@/states/qr.state";
import TypeInput from "../_shared/TypeInput";
const Menu = () => {
  const {
    data,
    pdf: pdfOn,
    setUserMenu,
    setPdf,
    setDelete
  } = useUserMenu();
  const {
    setIsRequest
  } = useIsRequest();
  const {
    handleGenerateResult
  } = useHandleGenerateResult();
  const {
    mutateHomeQr,
    mutateLoading,
    isSuccess
  } = useGenerateHomeQr();
  const signupModalState = useSignUpModal();
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const {
    config,
    setFiles,
    setPayload
  } = useConfig();
  const {
    insertQrData
  } = useQrShowState();
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("QrTypes.menu");
  const {
    error: validationErrors
  } = useError();
  const {
    errorMessage,
    setErrorMessage,
    handleFileRejectionErrors,
    hasFileUploadErr
  } = useFileUpload(t, setUserMenu);
  const {
    uploadFileData,
    uploadFile,
    isLoadingUploadFile
  } = useGuestFile();
  const {
    invalidType,
    largeFile
  } = errorMessage;
  const fileError = errors?.["files.pdf"];
  const isMobile = useMediaQuery({
    maxWidth: 1025
  });
  const [file, setFile] = useState<{
    file: File | null;
    name: string;
    size: string;
  }>();
  useEffect(() => {
    if (validationErrors) {
      setErrors(validationErrors ?? {});
    }
  }, [validationErrors]);
  useEffect(() => {
    if (signupModalState.isTriedDownload && !mutateLoading && isValidObject(validationErrors)) {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }, [signupModalState.isTempTrigger, mutateLoading, validationErrors]);
  useEffect(() => {
    handleGenerateResult(null);
    setIsRequest(true);
    setDelete("name");
    setPayload({
      redirect_type: "url"
    });
  }, []);

  // Handle debounced change
  const debouncedMutate = useCallback(debounce((updatedData: any) => {
    const {
      id,
      redirect_type,
      url,
      screenshot
    } = updatedData;
    const params = pdfOn ? {
      files: {
        pdf: pdfOn ? id : null
      }
    } : {
      url,
      screenshot
    };
    const reqData = {
      params: {
        redirect_type: redirect_type || "url",
        ...params
      },
      configParams: config
    };
    mutateHomeQr(reqData);
  }, 500), [mutateHomeQr, config, pdfOn]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setErrors({});
    setErrorMessage({
      invalidType: "",
      largeFile: ""
    });
    const withHttpsVal = value;
    const updatedData = {
      ...data,
      [name]: value
    };
    if (name === "redirect_type") {
      setPdf(value === "pdf");
      setDelete(value === "url" ? "pdf" : "url");
    }
    if (name === "redirect_type") {
      if (value === "url") {
        setUserMenu({
          [name]: value,
          isDownload: withHttpsVal !== ""
        });
        setFiles({});
        // Merge the existing data with the new data
        insertQrData("hasFileError", false);
      } else if (value === "pdf") {
        setUserMenu({
          [name]: value,
          isDownload: !!file?.file
        });
      }
    } else {
      setUserMenu({
        [name]: withHttpsVal,
        isDownload: !(withHttpsVal === "" && !isValidURL(withHttpsVal))
      });
    }
    if (name === "redirect_type") {
      setPayload({
        redirect_type: value
      });
    } else {
      debouncedMutate(updatedData);
    }
  };
  const optimizedFn = useCallback(debounce(handleChange, 500), []);
  useEffect(() => {
    setDelete("isDownload");
    signupModalState.setIsTriedDownload(false);
    if (pdfOn) {
      setDelete("url");
    } else {
      setDelete("pdf");
      setDelete("pdf_name");
      setDelete("pdf_size");
      setDelete("id");
      setFiles({});
    }
  }, [pdfOn]);
  let upload = 0;
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    noClick: isLoadingUploadFile,
    accept: {
      "application/pdf": [".pdf"]
    },
    maxSize: maxFileUploadSize,
    multiple: false,
    onDrop: (e: File[], fileRejections: unknown[]) => {
      setErrorMessage({
        largeFile: "",
        invalidType: ""
      });
      setDelete("pdf");
      setFiles({
        pdf: null
      });
      handleFileRejectionErrors(fileRejections);
      setErrors({});
      const file = e[0];
      if (file) {
        const {
          name,
          size
        } = file;
        setFile({
          file,
          name,
          size: bytesToSize(size)
        });
        if (!isLoadingUploadFile && upload < 1) {
          upload++;
          const formData = new FormData();
          formData.append("pdf", file);
          formData.append("type", forRender.pdf.toLowerCase());
          uploadFile(formData);
        }
      }
    }
  });
  useEffect(() => {
    if (!isLoadingUploadFile && uploadFileData && !!uploadFileData.data["pdf"]?.id) {
      setUserMenu({
        id: uploadFileData?.data["pdf"].id,
        pdf: uploadFileData?.data["pdf"].path,
        pdf_name: uploadFileData?.data["pdf"].originalName,
        pdf_size: bytesToSize(Number(uploadFileData?.data["pdf"].size)),
        isDownload: true
      });
      setFiles({
        pdf: uploadFileData?.data["pdf"].id
      });
    }
  }, [isLoadingUploadFile]);
  useEffect(() => {
    if (data.isDownload && data.pdf) {
      debouncedMutate(data);
    }
  }, [data.isDownload, data.pdf]);
  const renderPdfForNew = () => {
    return <IFThen condition={isLoadingUploadFile} Then={<div className={cn(classes.ShowMp3Data, {
      [classes.success]: isLoadingUploadFile
    })}>
            <div className={classes.ShowMp3Title}>
              <img src="types/pdf.svg" alt={"pdf"} />
              {file?.name}
            </div>
            <div className={classes.ShowMp3Upload}>
              <div className={classes.ShowMp3UploadTitle}>
                <div className={classes.ChangeFile}>
                  <span>
                    {t("fileSize")}: {file?.size}
                  </span>
                  <div className={classes.File} {...getRootProps()}>
                    <IFThen condition={isLoadingUploadFile} Then={<IFThen condition={isMobile} Then={<span className={classes.fileLoading}>Loading...</span>} Else={<LoadingDots bgColor="#A1A1A1" color="#575757" />} />} Else={<>
                          <When condition={!isMobile}>
                            <AiOutlineFilePdf size={17} />
                          </When>
                          {t("change")}
                          <input {...getInputProps()} />
                        </>} />
                  </div>
                </div>
              </div>
            </div>
          </div>} Else={<IFThen condition={!!data.pdf} Then={<div className={cn(classes.ShowMp3Data, {
      [classes.success]: !!data.pdf
    })}>
                <div className={classes.ShowMp3Title}>
                  <img src="types/pdf.svg" alt={"pdf"} />
                  {file?.name}
                </div>
                <div className={classes.ShowMp3Upload}>
                  <div className={classes.ShowMp3UploadTitle}>
                    <div className={classes.ChangeFile}>
                      <span>
                        {t("fileSize")}: {file?.size}
                      </span>
                      <div className={classes.File} {...getRootProps()}>
                        <>
                          <When condition={!isMobile}>
                            <AiOutlineFilePdf size={17} />
                          </When>
                          {t("change")}
                          <input {...getInputProps()} />
                        </>
                      </div>
                    </div>
                  </div>
                </div>
              </div>} Else={<>
                <div className={cn(classes.DropBox, {
        [classes.validErrorSetupDashed]: hasTrue(!!fileError?.length, hasFileUploadErr, !!errors?.["files.pdf"])
      })}>
                  <div {...getRootProps()} className={classes.DropArea}>
                    <div className="padBox">
                      <p className={classes.AddMp3}>
                        <FiUploadCloud size={17} />
                        <span>{t("input.placeholder")}</span>
                        <input {...getInputProps()} />
                      </p>
                      <span className={classes.FileSize}>{t("input.maxFileSize")}</span>
                    </div>
                    <When condition={isLoadingUploadFile}>
                      <LoadingDots bgColor="#A1A1A1" />
                    </When>
                  </div>
                </div>

                <When condition={everyTrue(invalidType, !largeFile)}>
                  <Error mt={8}>
                    <BiErrorCircle color="#F25A29" size={16} />
                    {invalidType}
                  </Error>
                </When>

                <When condition={!!largeFile && !errors?.["files.pdf"]}>
                  <Error mt={8}>
                    <BiErrorCircle color="#F25A29" size={16} />
                    {largeFile}
                  </Error>
                </When>


                <When condition={!!errors?.["files.pdf"]}>
                  <HomePageError withIcon={false} errors={errors?.["files.pdf"]} />
                </When>
              </>} />} data-sentry-element="IFThen" data-sentry-component="renderPdfForNew" data-sentry-source-file="Menu.tsx" />;
  };
  return <div className={classes.Wrapper} data-sentry-component="Menu" data-sentry-source-file="Menu.tsx">
      <div className={classes.Header}>
        <span className={classes.step}>1</span>
        <p className={classes.TopTitle}>{t("title")}</p>
      </div>
      <div className={classes.RadioBox}>
        <span className={classes.RadiBoxTitle}>{t("chooseFormat")}:</span>
        <div className={classes.RadioBoxItemsBox}>
          {["url", "pdf"].map(elem => {
          return <div className={classes.RadioBoxItem}>
                  <TypeInput label={elem.toUpperCase()} type={"radio"} name="redirect_type" value={elem} onChange={handleChange} checked={data.redirect_type === elem} />
                </div>;
        })}
        </div>
      </div>
      {pdfOn ? <>
          <div className={classes.Title}>{t("description")}</div>
          {renderPdfForNew()}
        </> : <></>}
      {!pdfOn ? <TypeInput label={t("input.label")} isRequired inputRef={inputRef} placeholder={`https://www.${t("input.message")}.com`} name="url" onChange={e => optimizedFn.call(null, e)} defaultValue={data.url} errors={errors?.["url"]} /> : <></>}
    </div>;
};
export default Menu;